import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Dialog } from 'tdesign-react';
import Cropper from 'react-easy-crop';

export default function CropModal({ visible, img, onClose, cropWidth, cropHeight, onConfirm }) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [source, setSource] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);

  const onCropComplete = async (croppedArea, croppedAreaPixels) => {
    if (!source) return;
    const cropBase64 = await getCroppedImg(source, croppedAreaPixels, { cropWidth, cropHeight });
    setCroppedImage(cropBase64);
  };

  // 关闭后重新初始化
  useEffect(() => {
    if (visible) {
      setTimeout(() => setSource(img), 300);
    }
    return () => {
      setSource(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setCroppedImage(null);
    };
  }, [visible, img]);

  return (
    <Dialog
      destroyOnClose
      width={1000}
      visible={visible}
      header={`裁剪(${cropWidth}x${cropHeight})`}
      onClose={onClose}
      placement='center'
      onConfirm={() => onConfirm(croppedImage)}
    >
      <SubTitle>
        若图片缩放后小于裁剪框，周边自动填充透明色。裁剪后的图片会自动压缩(2x@webp)。
      </SubTitle>
      <Root>
        {source && (
          <CropRoot>
            <Cropper
              image={source}
              crop={crop}
              zoom={zoom}
              aspect={cropWidth / cropHeight}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              restrictPosition={false}
              minZoom={0.1}
              maxZoom={10}
            />
          </CropRoot>
        )}
        <ReviewRoot>
          {croppedImage && (
            <>
              <ReviewImage src={croppedImage} $width={cropWidth} $height={cropHeight} />
              <Tip>预览</Tip>
            </>
          )}
        </ReviewRoot>
      </Root>
    </Dialog>
  );
}

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width: Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height: Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

// 创建图片，导出2x图
async function getCroppedImg(
  imageSrc,
  pixelCrop,
  cropSize,
  rotation = 0,
  flip = { horizontal: false, vertical: false },
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  const { cropWidth, cropHeight } = cropSize;

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(image.width, image.height, rotation);

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');
  // 设置 2x 大小
  croppedCanvas.width = cropWidth * 2;
  croppedCanvas.height = cropHeight * 2;

  const croppedCtx = croppedCanvas.getContext('2d');
  croppedCtx.fillStyle = 'rgba(0,0,0,0)';
  croppedCtx.fillRect(0, 0, croppedCanvas.width, croppedCanvas.height);

  if (!croppedCtx) {
    return null;
  }

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    croppedCanvas.width,
    croppedCanvas.height,
  );

  // As Base64 string
  return croppedCanvas.toDataURL('image/webp');

  // As a blob
  // return new Promise((resolve, reject) => {
  //   croppedCanvas.toBlob((file) => {
  //     resolve(URL.createObjectURL(file))
  //   }, 'image/jpeg')
  // })
}

const SubTitle = styled.div`
  color: var(--td-text-color-secondary);
  margin-bottom: 10px;
`;

const Root = styled.div`
  display: flex;
  align-items: center;
  height: 550px;
`;

const CropRoot = styled.div`
  flex: 1;
  height: 100%;
  position: relative;
  border: 2px solid rgba(0, 0, 0, 0.5);
`;

const ReviewRoot = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

const Tip = styled.div`
  width: 100%;
  text-align: center;
  font-size: 12px;
  line-height: 18px;
  color: var(--td-font-gray-2);
  margin-top: 8px;
`;

const ReviewImage = styled.img`
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  border-radius: 2px;
  overflow: hidden;
  box-shadow: 0px 3px 5px -3px rgba(0, 0, 0, 0.4), 0px 3px 5px rgba(0, 0, 0, 0.4);
`;
